import { Paper, Title, ScrollArea, Timeline, Text, Badge, Group, Stack, Loader, Center } from '@mantine/core';
import { formatDistanceToNow } from 'date-fns';
import { Radio, Camera, Car, MapPin } from 'lucide-react';
import type { SensorObservation } from '../types';

interface SensorActivityFeedProps {
  observations: SensorObservation[];
  loading?: boolean;
}

export function SensorActivityFeed({ observations, loading = false }: SensorActivityFeedProps) {
  const formatTime = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'just now';
    }
  };

  const getObservationIcon = (obs: SensorObservation) => {
    if (obs.has_plate) return <Car size={16} />;
    if (obs.face_count > 0) return <Camera size={16} />;
    return <Radio size={16} />;
  };

  const getObservationColor = (obs: SensorObservation): string => {
    if (obs.has_plate) return 'green';
    if (obs.face_count > 0) return 'blue';
    return 'grape';
  };

  const getObservationType = (obs: SensorObservation): string => {
    const types: string[] = [];
    if (obs.has_plate) types.push('Plate');
    if (obs.face_count > 0) types.push(`${obs.face_count} Face${obs.face_count > 1 ? 's' : ''}`);
    if (obs.device_count > 0) types.push(`${obs.device_count} Signal${obs.device_count > 1 ? 's' : ''}`);
    return types.length > 0 ? types.join(', ') : 'Observation';
  };

  if (loading) {
    return (
      <Paper shadow="sm" p="md" withBorder>
        <Title order={4} mb="md">Sensor Activity Feed</Title>
        <Center h={200}>
          <Loader size="md" />
        </Center>
      </Paper>
    );
  }

  if (observations.length === 0) {
    return (
      <Paper shadow="sm" p="md" withBorder>
        <Title order={4} mb="md">Sensor Activity Feed</Title>
        <Text c="dimmed" size="sm" ta="center" py="xl">
          No sensor observations yet. Waiting for activity...
        </Text>
      </Paper>
    );
  }

  return (
    <Paper shadow="sm" p="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={4}>Sensor Activity Feed</Title>
        <Badge color="blue" variant="light">
          {observations.length} observations
        </Badge>
      </Group>

      <ScrollArea h={500} offsetScrollbars>
        <Timeline active={observations.length} bulletSize={24} lineWidth={2}>
          {observations.map((obs) => {
            const color = getObservationColor(obs);
            return (
              <Timeline.Item
                key={obs.observation_id}
                bullet={getObservationIcon(obs)}
                title={
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      {obs.sensor_id}
                    </Text>
                    <Badge size="xs" color={color} variant="dot">
                      {getObservationType(obs)}
                    </Badge>
                  </Group>
                }
                color={color}
              >
                <Stack gap="xs">
                  {obs.location && (
                    <Group gap={4}>
                      <MapPin size={12} />
                      <Text size="xs" c="dimmed">
                        {obs.location.lat.toFixed(4)}, {obs.location.lon.toFixed(4)}
                      </Text>
                    </Group>
                  )}
                  <Text size="xs" c="dimmed">
                    {formatTime(obs.timestamp)}
                  </Text>
                </Stack>
              </Timeline.Item>
            );
          })}
        </Timeline>
      </ScrollArea>
    </Paper>
  );
}
